import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import type { PaymentRequest } from "@stripe/stripe-js";
import { Radio, RadioGroup, Text } from "@ui-kitten/components";
import "./index.css";
import Button from "components/Button";
import AgreementText from "components/AgreementText";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import i18n from "plugins/localization";
import toast from "utils/toast";
import {
  StripeTokenError,
  type StripePaymentFormProps,
  StripeTokenResponse,
} from "./StripePaymentForm";

type PaymentMethodChoice = "wallet" | "card";

const Form: React.FC<StripePaymentFormProps> = (props) => {
  const {
    btnText = "Confirm Purchase",
    onTokenReceived,
    disabled,
    loading,
    showAgreementText = true,
    beforeButton,
    walletAmount,
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const [fetchingToken, setFetchingToken] = useState(false);
  const [nameOnCard, setNameOnCard] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null
  );
  // Wallet brand names are trademarks and are not translated.
  const [walletName, setWalletName] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethodChoice>("card");

  // When wallet availability resolves we default to it, but never away from a
  // card form the user has already started filling in.
  const cardTouchedRef = useRef(false);

  // The PaymentRequest token listener lives as long as the PaymentRequest itself,
  // so it must read the current handler through a ref: this form can be mounted
  // (hidden) steps before checkout completes, and calling a handler captured at
  // mount would run the purchase against stale checkout state.
  const onTokenReceivedRef = useRef(onTokenReceived);
  onTokenReceivedRef.current = onTokenReceived;

  // A purchase amount that has not finished loading (null, or NaN from parsing
  // missing data) must never reach the wallet sheet: Apple Pay renders the
  // pending hint as "Amount Pending" and ignores update() once the sheet is
  // open.  The wallet button stays blocked until the amount is real.  A real
  // amount of 0 is NOT loading: the sheet shows $0.00 and stays usable.
  const walletAmountLoading =
    walletAmount === null ||
    (typeof walletAmount === "number" && Number.isNaN(walletAmount));

  // walletAmount undefined means a save-card context, where the $0 pending
  // verification sheet is the intended behavior.
  const walletSheetTotal =
    walletAmount === undefined || walletAmountLoading
      ? {
          label: i18n.t(
            "components.StripePaymentForm.savePaymentMethod"
          ) as string,
          amount: 0,
          pending: true,
        }
      : {
          label: i18n.t("components.StripePaymentForm.total") as string,
          // Stripe deals in whole cents only!
          amount: Math.round(walletAmount * 100),
        };
  const walletSheetTotalRef = useRef(walletSheetTotal);
  walletSheetTotalRef.current = walletSheetTotal;

  useEffect(() => {
    if (!stripe || !tenantConfig?.walletPaymentsEnabled) {
      return;
    }
    let active = true;
    const pr = stripe.paymentRequest({
      country: "US",
      currency: (tenantConfig.defaultCurrency || "USD").toLowerCase(),
      total: walletSheetTotalRef.current,
      requestPayerName: true,
      // Wallets only; the card path below already covers everything Link offers.
      ...({ disableLink: true } as object),
    });
    pr.on("token", async (ev) => {
      const payerName = ev.payerName ?? (ev.token as any)?.card?.name ?? "";
      const payerZip = (ev.token as any)?.card?.address_zip ?? "";
      // The backend requires both fields (@NotBlank), so reject inside the sheet
      // rather than letting the save fail after the sheet has reported success.
      if (!payerName || !payerZip) {
        ev.complete("fail");
        toast.error(
          i18n.t("components.StripePaymentForm.walletMissingBillingDetails")
        );
        return;
      }
      try {
        const outcome = await onTokenReceivedRef.current(undefined, {
          token: ev.token,
          nameOnCard: payerName,
          postalCode: payerZip,
        } as unknown as StripeTokenResponse);
        // "completed", "deferred" and legacy void returns all close the sheet
        // as success; only an explicit "failed" (or a throw) shows failure.
        ev.complete(outcome === "failed" ? "fail" : "success");
      } catch {
        // The consumer surfaces its own error; the sheet just shows failure.
        ev.complete("fail");
      }
    });
    pr.canMakePayment().then((result) => {
      if (active && (result?.applePay || result?.googlePay)) {
        setPaymentRequest(pr);
        setWalletName(result.applePay ? "Apple Pay" : "Google Pay");
        if (!cardTouchedRef.current) {
          setMethod("wallet");
        }
      }
    });
    return () => {
      active = false;
      setPaymentRequest(null);
      setWalletName(null);
      setMethod("card");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, tenantConfig?.walletPaymentsEnabled]);

  useEffect(() => {
    paymentRequest?.update({ total: walletSheetTotal });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRequest, walletSheetTotal.amount, walletSheetTotal.label]);

  const requestStripeToken = async () => {
    setFetchingToken(true);
    const response = (await stripe!.createToken(
      elements!.getElement("cardNumber")!
    )) as any;

    setFetchingToken(false);
    if (response.error) {
      onTokenReceived(response as StripeTokenError);
    } else {
      try {
        await onTokenReceived(undefined, {
          ...response,
          nameOnCard,
          postalCode,
        } as StripeTokenResponse);
      } catch {
        // Consumers surface their own errors; catching keeps the rejection handled.
      }
    }
  };

  const payingWithWallet = !!paymentRequest && method === "wallet";

  return (
    <View>
      {paymentRequest && (
        <View style={styles.methodSelector}>
          <Text style={styles.label}>
            {i18n.t("components.StripePaymentForm.payWith")}
          </Text>
          <RadioGroup
            style={styles.methodOptions}
            selectedIndex={method === "wallet" ? 0 : 1}
            onChange={(index) => setMethod(index === 0 ? "wallet" : "card")}
          >
            <Radio testID="payment-method-wallet">{walletName ?? ""}</Radio>
            <Radio testID="payment-method-card">
              {i18n.t("components.StripePaymentForm.creditCard") as string}
            </Radio>
          </RadioGroup>
        </View>
      )}

      {!payingWithWallet && (
        <div className="stripe-elements">
          <View style={styles.field}>
            <Text style={styles.label}>Name on card</Text>
            <div className="StripeElement custom-input ">
              <input
                type="text"
                value={nameOnCard}
                onChange={(e) => {
                  cardTouchedRef.current = true;
                  setNameOnCard(e.target.value);
                }}
                data-testid="name-on-card"
              ></input>
            </div>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Card Number</Text>
            <CardNumberElement options={{ placeholder: "" }} />
          </View>

          <View className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
            <View style={styles.field}>
              <Text style={styles.label}>Expiration Date</Text>
              <CardExpiryElement options={{ placeholder: "" }} />
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>CVC</Text>
              <CardCvcElement options={{ placeholder: "" }} />
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Postal Code</Text>
            <div className="StripeElement custom-input ">
              <input
                type="text"
                value={postalCode}
                onChange={(e) => {
                  cardTouchedRef.current = true;
                  setPostalCode(e.target.value);
                }}
                data-testid="postal-code"
              ></input>
            </div>
          </View>
        </div>
      )}

      {showAgreementText && (
        <AgreementText
          btnText={payingWithWallet ? walletName ?? btnText : btnText}
          style={{ marginVertical: 30 }}
        />
      )}
      {beforeButton}
      {payingWithWallet ? (
        <View
          style={[
            styles.walletButton,
            (disabled || loading || walletAmountLoading) &&
              styles.walletButtonDisabled,
          ]}
          pointerEvents={
            disabled || loading || walletAmountLoading ? "none" : "auto"
          }
        >
          <PaymentRequestButtonElement
            options={{ paymentRequest: paymentRequest! }}
            onClick={(ev) => {
              // The wallet sheet completes a purchase in one gesture, so gates like the
              // auto-renewal consent checkbox must be satisfied before it can open.
              if (disabled || loading || fetchingToken || walletAmountLoading) {
                ev.preventDefault();
              }
            }}
          />
        </View>
      ) : (
        <Button
          testID="confirm-purchase-button"
          disabled={
            disabled ||
            loading ||
            fetchingToken ||
            nameOnCard === "" ||
            postalCode === ""
          }
          onPress={requestStripeToken}
          loading={fetchingToken || loading}
          style={{ marginTop: 10, marginBottom: 20 }}
        >
          {btnText}
        </Button>
      )}
    </View>
  );
};
const styles = StyleSheet.create({
  field: {
    marginBottom: 15,
  },
  label: {
    fontSize: 12,
    fontWeight: "500",
    marginBottom: 4,
    color: "black",
  },
  methodSelector: {
    marginBottom: 20,
  },
  methodOptions: {
    flexDirection: "row",
  },
  walletButton: {
    marginTop: 10,
    marginBottom: 20,
  },
  walletButtonDisabled: {
    opacity: 0.4,
  },
});
export default Form;
